<template>
  <van-button
      :disabled="isBtnDisabled"
      :loading="loading"
      block
      type="primary"
      @click="addToCart"
  >
    {{ getButtonText }}
  </van-button>
</template>

<script setup>
import {addCartQuery, addMultipleCartMutation, addWishlistMutation, removeWishlistMutation,} from "@/plugins/API/cart";
import {useCartStore} from "@/store/cart";
import {useWishlistStore} from "@/store/wishlist";
import {EVENTS, TRACKERS} from "@/constants/events";

const {hasDifferPowerOptions} = storeToRefs(useCartStore());
const {$resetProductAttributes} = useCartStore();

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  selectedOption: {
    type: Object,
    default: null,
  },
  selectedOptionsAttributes: {
    type: Object,
    default: null,
  },
  data: {
    type: Object,
    default: {},
  },
  sourceData: {
    type: Object,
    default: {}
  }
});

onUnmounted(() => {
  $resetProductAttributes()
})

const {t} = useI18n();
const {isLoading: addToCartLoading, mutateAsync: cartMutateAsync} =
    addCartQuery();
const {
  isLoading: addMultipleToCartLoading,
  mutateAsync: cartMultpleMutateAsync,
} = addMultipleCartMutation();
const {isLoading: addWishlistLoading, mutateAsync: addWishlistMutateAsync} =
    addWishlistMutation();
const {
  isLoading: removeWishlistLoading,
  mutateAsync: removeWishlistMutateAsync,
} = removeWishlistMutation();
const addToCart = () => {

  if (outOfStock.value) {
    $resetProductAttributes()
    return inWishlist.value
        ? removeWishlistMutateAsync({id: props.id})
        : addWishlistMutateAsync({id: props.id});

  }
  const {selectedOption, data, selectedOptionsAttributes} = props;
  const {product_option_value_id} = selectedOption || {};
  const {options, id} = data;
  const {defaultOptionQuantity, secondaryOptionQuantity, secondaryOption} =
      selectedOptionsAttributes;

  const option = product_option_value_id && {
    [options[0]?.product_option_id]: product_option_value_id,
  };

  const optionTwo = secondaryOption?.product_option_value_id && {
    [options[0]?.product_option_id]:
    secondaryOption?.product_option_value_id,
  };

  if (secondaryOption) {
    const multipleProductsAttributes = [
      {product_id: id, quantity: defaultOptionQuantity, option},
      {
        product_id: id,
        quantity: secondaryOptionQuantity,
        option: optionTwo,
      },
    ];

    cartMultpleMutateAsync({params: multipleProductsAttributes})
    return;
  }

  const route = useRoute()
  const {emit: triggeredLikeProducts} = useEventBus(EVENTS.LIKE_PRODUCTS)
  cartMutateAsync({id, option, quantity: defaultOptionQuantity}).then(
      ({data}) => {

        // For Algolia convertedObjectIDsAfterSearch
        if (route.query?.queryID) track(TRACKERS.ALGOLIA_ADD_TO_CART, {
          id,
          queryID: route.query?.queryID
        })
        // Track add to cart events
        track(TRACKERS.ADD_TO_CART, {
          product: props.data,
          option: props.selectedOption,
          sourceData: props?.sourceData
        });
        track(TRACKERS.CTA_CLICK, {eventAction: 'Add to bag', eventLabel: '', product: props.product})

        if (data.cart?.products_you_may_like?.length) {
          triggeredLikeProducts({
            products: data.cart?.products_you_may_like,
            total: data.cart?.total,
          });
        }
      }
  );
};

const hasOptionsNotSelected = computed(
    () => !!props.data?.has_option && !props.selectedOption?.price
);

const selectedAttributesOptionsIsActive = computed(() => {
  if (props.data?.has_diff_power_options && hasDifferPowerOptions.value) return !props.selectedOptionsAttributes.secondaryOption
});

const outOfStock = computed(
    () =>
        !props?.data?.has_stock ||
        (props.data?.has_option && !props.selectedOption?.has_stock)
);

const wishlistStore = useWishlistStore();
const inWishlist = computed(
    () =>
        !!wishlistStore.products.find(
            (item) => Number(item.product_id) === Number(props.id)
        )
);

const getButtonText = computed(() => {
  if (hasOptionsNotSelected.value) {
    return t("product.btn.pick_an_option");
  }
  if (outOfStock.value) {
    return inWishlist.value
        ? t("product.btn.youll_be_notified")
        : t("product.btn.notify_me");
  }
  return t("product.btn.add_to_bag");
});

const isBtnDisabled = computed(
    () =>
        addToCartLoading.value ||
        hasOptionsNotSelected.value ||
        selectedAttributesOptionsIsActive.value
);
const loading = computed(
    () =>
        addToCartLoading.value ||
        addWishlistLoading.value ||
        removeWishlistLoading.value ||
        addMultipleToCartLoading.value ||
        false
);
</script>

<style scoped></style>
